'use client';

import SubmitButton from '@/components/common/button/submit-button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import useActionHandler from '@/hooks/useActionHandler';
import { contactAction } from '@/services/contact/contact';
import { contactSchemaZodValidation } from '@/zod/contact';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

type FormValues = z.infer<typeof contactSchemaZodValidation>;

const ContactForm = () => {
  const { executePost } = useActionHandler();
  const form = useForm<FormValues>({
    resolver: zodResolver(contactSchemaZodValidation),
    defaultValues: {
      name: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    await executePost({
      action: () => contactAction(data),
      success: {
        onSuccess: () => {
          form.reset();
        },
        message: 'Message sent successfully.',
        loadingText: 'Message sending to the authority...',
      },
      errorMessage: 'Failed to send message.',
    });
  };

  return (
    <Card
      className="border-none bg-white/50 shadow-lg backdrop-blur-sm"
      data-aos="fade-up"
      data-aos-delay="200"
    >
      <CardHeader>
        <CardTitle className="text-2xl font-bold text-gray-900 dark:text-white">
          Send us a Message
        </CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Top row: Name + Email */}
            <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="John Doe"
                        autoComplete="name"
                        className="bg-white dark:bg-slate-900/50"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="sr-only">
                      Your full name
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Email</FormLabel>
                    <FormControl>
                      <Input
                        type="email"
                        placeholder="john@company.com"
                        autoComplete="email"
                        className="bg-white dark:bg-slate-900/50"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription className="sr-only">
                      Your email address
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Subject (full width) */}
            <FormField
              control={form.control}
              name="subject"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Subject</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="How can we help?"
                      className="bg-white dark:bg-slate-900/50"
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="sr-only">
                    The topic of your message
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Message (full width) */}
            <FormField
              control={form.control}
              name="message"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Message</FormLabel>
                  <FormControl>
                    <Textarea
                      className="h-32 resize-none bg-white dark:bg-slate-900/50"
                      placeholder="Write your message here..."
                      {...field}
                    />
                  </FormControl>
                  <FormDescription className="sr-only">
                    Your full message
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="pt-2">
              <SubmitButton
                text="Send Message"
                loading={form.formState.isSubmitting}
                className="w-full"
              />
            </div>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
